import type { ReploSimpleProject } from "schemas/generated/project";

import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import { Loader } from "@editor/components/common/Loader";
import SettingsCard from "@editor/components/shops-ui/settings/SettingsCard";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { useOpenModal } from "@editor/hooks/useModal";

import InlineAlert from "@replo/design-system/components/alert/InlineAlert";
import Button from "@replo/design-system/components/button/Button";

import { useUpdateShop } from "../../../../../features/shops/hooks/useUpdateShop";

const ShopSettingsForm: React.FC<{
  project: ReploSimpleProject;
}> = ({ project }) => {
  const { updateShopName } = useUpdateShop();
  const [name, setName] = React.useState(project.name ?? "");

  const updateSettingsSections = [
    {
      title: "Shop Name",
      component: (
        <NameInput initialName={name} onChange={(value) => setName(value)} />
      ),
    },
  ];

  const handleWorkspaceUpdate = async () => {
    await updateShopName({
      id: project.id,
      name,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      {updateSettingsSections.map((section) => {
        return (
          <div key={section.title}>
            <div className="text-base font-medium mb-1.5">{section.title}</div>
            {section.component}
          </div>
        );
      })}
      <div className="flex w-full flex-row justify-end">
        <Button
          variant="primary"
          size="base"
          onClick={() => void handleWorkspaceUpdate()}
          disabled={name === project.name}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

const NameInput = ({
  initialName,
  onChange,
}: {
  initialName: string;
  onChange: (value: string) => void;
}) => {
  return (
    <div className="flex flex-col gap-2">
      <Input
        value={initialName}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter your shop name"
        size="base"
      />
    </div>
  );
};

type ShopSectionProps = {
  project: ReploSimpleProject;
};

const ShopMoveSection: React.FC<ShopSectionProps> = ({ project }) => {
  const openModal = useOpenModal();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex w-full flex-row justify-end">
        <Button
          variant="primary"
          size="base"
          onClick={() => {
            if (project.ownerWorkspace?.id) {
              openModal({
                type: "moveProjectModal",
                props: {
                  projectId: project.id,
                  oldWorkspaceId: project?.ownerWorkspace?.id,
                  shopifyUrl: project.integrations?.shopify?.store.shopifyUrl,
                },
              });
            }
          }}
        >
          Move
        </Button>
      </div>
    </div>
  );
};

const ShopDeleteSection: React.FC<ShopSectionProps> = ({ project }) => {
  const openModal = useOpenModal();

  return (
    <div className="flex flex-col gap-2">
      <InlineAlert variant="error" multiline>
        Deleting this shop will permanently erase it. The Replo Team can assist
        with recovery if needed.
      </InlineAlert>
      <div className="flex w-full flex-row justify-end">
        <Button
          variant="danger"
          size="base"
          onClick={() => {
            openModal({
              type: "deleteProjectModal",
              props: {
                projectId: project.id,
                from: "list",
              },
            });
          }}
        >
          Delete Shop
        </Button>
      </div>
    </div>
  );
};

type SettingsContentProps = {
  title?: string;
  description?: string;
};

const SettingsContent: React.FC<
  React.PropsWithChildren<SettingsContentProps>
> = ({ children, title, description }) => {
  return (
    <div className="flex flex-col gap-3 w-full grow shrink-1">
      <div className="flex flex-col">
        {title && <span className="text-lg font-semibold">{title}</span>}
        {description && (
          <span className="text-muted text-sm">{description}</span>
        )}
      </div>
      {children}
    </div>
  );
};

type ShopSettingsProps = {
  sections: {
    title?: string;
    component: React.ReactNode;
    description?: string;
  }[];
};

const LoadedShopSettings = ({ sections }: ShopSettingsProps) => {
  return (
    <>
      {sections.map((section, index) => (
        <SettingsCard key={index} className="w-[616px]">
          <SettingsContent
            title={section.title}
            description={section.description}
          >
            {section.component}
          </SettingsContent>
        </SettingsCard>
      ))}
    </>
  );
};

const SettingsLayout: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  return <div className="flex w-full flex-col gap-4">{children}</div>;
};

const ShopSettingsWrapper = () => {
  const { project, isLoading: isLoadingProject } = useCurrentProjectContext();

  const isLoading = isLoadingProject;

  if (!project) {
    return null;
  }

  const sections = [
    {
      title: "Shop Settings",
      component: <ShopSettingsForm project={project} />,
    },
    {
      title: "Move Shop",
      component: <ShopMoveSection project={project} />,
      description: "Move this shop to a different workspace.",
    },
    {
      title: "Delete Shop",
      component: <ShopDeleteSection project={project} />,
    },
  ];

  return (
    <SettingsLayout>
      {isLoading ? <Loader /> : <LoadedShopSettings sections={sections} />}
    </SettingsLayout>
  );
};

export default ShopSettingsWrapper;
